import { useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import CheckIcon from '@mui/icons-material/Check';
import CallIcon from '@mui/icons-material/Call';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import Eyebrow from '../components/common/Eyebrow';
import StatusBadge from '../components/common/StatusBadge';
import EmptyState from '../components/common/EmptyState';
import LiveTrackingMap from '../components/tracking/LiveTrackingMap';
import { TrackShimmer } from '../components/shimmer';
import { bookingsApi, useGetBookingQuery } from '../store/bookingsApi';
import { cartApi } from '../store/cartApi';
import { buildItdoseReportPdfUrl, logReportView } from '../utils/fetchItdoseReports';
import useBookingLocationStream from '../hooks/useBookingLocationStream';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { ROUTES, buildRoute } from '../constants/routes';

// Cancellation eligibility: > 3 hours before the slot, not collected/reported/cancelled.
const canCancel = (booking) => {
  if (!booking) return false;
  if (['cancelled', 'collected', 'reported'].includes(booking.status)) return false;
  const sched = booking.schedule;
  if (!sched?.date) return true;
  const m = String(sched.slot || '').match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  let h = m ? Number(m[1]) : 0;
  const mn = m ? Number(m[2]) : 0;
  const ap = (m?.[3] || '').toUpperCase();
  if (ap === 'PM' && h < 12) h += 12;
  if (ap === 'AM' && h === 12) h = 0;
  const [y, mo, d] = String(sched.date).split('-').map(Number);
  if (!y) return true;
  const at = new Date(y, mo - 1, d, h, mn).getTime();
  return (at - Date.now()) / 3_600_000 >= 3;
};

// Ordered timeline the user sees on this page.
const STAGES = [
  { key: 'confirmed', label: 'Booking Confirmed' },
  { key: 'assigned',  label: 'Trustian Assigned' },
  { key: 'enroute',   label: 'En Route' },
  { key: 'collected', label: 'Sample Collected' },
  { key: 'reported',  label: 'Report Ready' },
];

const STATUS_TO_INDEX = {
  confirmed: 0,
  assigned: 1,
  enroute: 2,
  collected: 3,
  reported: 4,
};

const Track = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get('id');
  const justPaid = searchParams.get('payment') === 'success';

  const dispatch = useDispatch();
  const { user } = useAuth();
  const { showToast } = useUI();

  const { data, isLoading, isError, error } = useGetBookingQuery(id, { skip: !id });
  const booking = data?.booking;

  // Coming back from PayU success: drop the cart badge to 0, refresh
  // the bookings list so the new one shows in My Bookings, surface a
  // toast, and strip the query so a refresh doesn't re-trigger.
  useEffect(() => {
    if (!justPaid) return;
    dispatch(cartApi.util.invalidateTags(['Cart']));
    dispatch(bookingsApi.util.invalidateTags(['Booking']));
    showToast('Payment successful — your booking is confirmed!');
    const next = new URLSearchParams(searchParams);
    next.delete('payment');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subscribe to the trustian's live position whenever the booking is in a
  // stage where the trustian is moving towards (or arriving at) the address.
  // We also pick up the last-known snapshot from the booking doc so the
  // map isn't blank while we wait for the first socket frame.
  const trackingEnabled = ['assigned', 'enroute'].includes(booking?.status);
  const initialTrustianLocation = useMemo(() => {
    const t = booking?.trustian_location;
    if (t && typeof t.lat === 'number' && typeof t.lng === 'number') {
      return { lat: t.lat, lng: t.lng };
    }
    return null;
  }, [booking]);
  const { location: liveLocation, isLive } = useBookingLocationStream({
    bookingId: booking?.booking_id,
    initialLocation: initialTrustianLocation,
    enabled: trackingEnabled,
  });

  // Patient pin comes from the booking's geocoded address. If the user
  // entered free-text with no coords, we hide the pin and the map just
  // centers on the trustian.
  const patientLocation = useMemo(() => {
    const p = booking?.patient?.location;
    if (p && typeof p.lat === 'number' && typeof p.lng === 'number') {
      return { lat: p.lat, lng: p.lng };
    }
    return null;
  }, [booking]);

  const stageIdx = useMemo(() => {
    if (!booking) return 0;
    return STATUS_TO_INDEX[booking.status] ?? 0;
  }, [booking]);

  // No id in URL → friendly prompt
  if (!id) {
    return (
      <Box sx={{ bgcolor: 'var(--cream)', minHeight: '90vh', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="sm">
          <EmptyState
            icon="🧭"
            title="No booking selected"
            description="Pick a booking from your dashboard to see its live status."
            action={
              <Button variant="contained" onClick={() => navigate(ROUTES.DASHBOARD)}>
                Go to My Bookings
              </Button>
            }
          />
        </Container>
      </Box>
    );
  }

  if (isLoading) {
    return <TrackShimmer />;
  }

  if (isError || !booking) {
    return (
      <Box sx={{ bgcolor: 'var(--cream)', minHeight: '90vh', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="sm">
          <EmptyState
            icon="⚠"
            title="Booking not found"
            description={error?.data?.message || `We couldn't find booking ${id}.`}
            action={
              <Button variant="contained" onClick={() => navigate(ROUTES.DASHBOARD)}>
                Back to My Bookings
              </Button>
            }
          />
        </Container>
      </Box>
    );
  }

  // Cancelled → simplified view
  if (booking.status === 'cancelled') {
    return (
      <Box sx={{ bgcolor: 'var(--cream)', minHeight: '90vh' }}>
        <Box sx={{ bgcolor: 'var(--forest)', color: '#fff', pt: { xs: 10, md: 12 }, pb: 5, px: { xs: 2, md: 4 } }}>
          <Container maxWidth="xl" sx={{ maxWidth: 1200 }}>
            <Eyebrow white>Cancelled Booking</Eyebrow>
            <Typography className="serif" variant="h2" sx={{ color: '#fff', mt: 1, mb: 1 }}>
              Booking {booking.booking_id}
            </Typography>
            <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.55)' }}>
              {booking.cancel_reason ? `Reason: ${booking.cancel_reason}` : 'This booking was cancelled.'}
            </Typography>
          </Container>
        </Box>
        <Container maxWidth="xl" sx={{ maxWidth: 1200, py: 4 }}>
          <Button variant="contained" onClick={() => navigate(ROUTES.DASHBOARD)}>
            ← Back to My Bookings
          </Button>
        </Container>
      </Box>
    );
  }

  const progress = ((stageIdx + 1) / STAGES.length) * 100;
  const currentStage = STAGES[stageIdx];
  const etaHint =
    booking.status === 'enroute' ? 'Arriving within ~15 minutes'
    : booking.status === 'assigned' ? 'Trustian will head out shortly'
    : booking.status === 'confirmed' ? 'Awaiting Trustian assignment'
    : booking.status === 'collected' ? 'Sample on its way to the lab'
    : booking.status === 'reported' ? 'Report is ready — check My Reports'
    : '';

  return (
    <Box sx={{ bgcolor: 'var(--cream)', minHeight: '90vh' }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'var(--forest)', color: '#fff', pt: { xs: 10, md: 12 }, pb: 5 }}>
        <Container maxWidth="xl" sx={{ maxWidth: 1200, px: { xs: 2, md: 4 } }}>
          <Eyebrow white>Live Tracking</Eyebrow>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mt: 1, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography className="serif" variant="h2" sx={{ color: '#fff', mb: 0.5 }}>
                {currentStage?.label || 'Your booking'}
              </Typography>
              <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }}>
                Booking <strong>{booking.booking_id}</strong>
                {' · '}Slot: <strong style={{ color: '#86EFAC' }}>{booking.schedule?.date} · {booking.schedule?.slot}</strong>
              </Typography>
            </Box>
            <StatusBadge tone="green">● LIVE</StatusBadge>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ maxWidth: 1200, py: { xs: 3, md: 4 }, px: { xs: 2, md: 4 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' },
            gap: { xs: 2, md: 3 },
            alignItems: 'start',
          }}
        >
          {/* Left — Map (dummy for now) + Trustian + Items */}
          <Box>
            <Box
              sx={{
                bgcolor: '#fff',
                border: '1px solid var(--border-l)',
                borderRadius: '20px',
                overflow: 'hidden',
                mb: 2.5,
              }}
            >
              {/* Live map — plots the trustian's streamed GPS + the
                  patient's geocoded collection pin. Shown whenever we
                  have at least one coordinate to render; falls back to
                  the previous decorative gradient when the booking has
                  no geocoded address AND no live point yet. */}
              <Box sx={{ height: { xs: 220, md: 300 }, position: 'relative' }}>
                {(patientLocation || liveLocation) ? (
                  <LiveTrackingMap
                    patientLocation={patientLocation}
                    trustianLocation={liveLocation}
                    hint={
                      booking.status === 'enroute'
                        ? (isLive ? '● Live — trustian on the way' : 'Reconnecting…')
                        : booking.status === 'assigned'
                          ? 'Trustian will start moving shortly'
                          : null
                    }
                  />
                ) : (
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'linear-gradient(135deg, #E7F5ED 0%, #C8EDD8 100%)',
                    }}
                  >
                    <Typography sx={{ fontSize: 13, color: 'var(--stone)', fontWeight: 600 }}>
                      Live map will appear once the trustian starts moving
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Trustian card — populated from booking.trustian_id. While
                  the booking is still 'confirmed' (no one assigned yet),
                  we show a neutral "looking for a trustian" placeholder
                  instead of a name/avatar that doesn't exist. */}
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                {booking.trustian_id ? (
                  (() => {
                    const t = booking.trustian_id;
                    const initials = t.initials
                      || (t.name || 'T').split(/\s+/).filter(Boolean).slice(0, 2)
                          .map((p) => p[0]?.toUpperCase()).join('')
                      || 'T';
                    const ratingLabel = Number(t.rating) > 0 ? `⭐ ${Number(t.rating).toFixed(1)}` : null;
                    const jobsLabel = Number(t.total_jobs) > 0 ? `${t.total_jobs} jobs` : null;
                    const metaBits = ['Trustian', t.trustlab_id ? `ID ${t.trustlab_id}` : null, ratingLabel, jobsLabel]
                      .filter(Boolean).join(' · ');
                    return (
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                          <Box
                            sx={{
                              width: 52, height: 52, borderRadius: '50%',
                              bgcolor: t.color || 'var(--sage)', color: '#fff',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 18, fontWeight: 700, flexShrink: 0,
                            }}
                          >
                            {initials}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{t.name || 'Trustian'}</Typography>
                            <Typography sx={{ fontSize: 12, color: 'var(--stone)', fontWeight: 300 }}>
                              {metaBits}
                            </Typography>
                          </Box>
                        </Stack>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<CallIcon />}
                          disabled={!t.phone}
                          component="a"
                          href={t.phone ? `tel:${t.phone}` : undefined}
                          sx={{ minWidth: { xs: '100%', sm: 120 } }}
                        >
                          Call
                        </Button>
                      </Stack>
                    );
                  })()
                ) : (
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 52, height: 52, borderRadius: '50%',
                        bgcolor: 'var(--border-l)', color: 'var(--stone)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 22, flexShrink: 0,
                      }}
                    >
                      🔍
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Finding a trustian</Typography>
                      <Typography sx={{ fontSize: 12, color: 'var(--stone)' }}>
                        You'll see the trustian's details here as soon as one is assigned.
                      </Typography>
                    </Box>
                  </Stack>
                )}
              </Box>
            </Box>

            {/* Booking items */}
            <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '20px', p: { xs: 2.5, md: 3 }, mb: 2.5 }}>
              <Typography className="serif" variant="h3" sx={{ mb: 1.5, fontSize: 18 }}>
                Tests booked ({(booking.items || []).length})
              </Typography>
              <Stack spacing={1.25}>
                {(booking.items || []).map((it) => (
                  <Stack
                    key={`${it.item_type}-${it.item_id}`}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ py: 1, borderBottom: '1px dashed var(--border-l)', '&:last-of-type': { border: 'none' } }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>{it.name}</Typography>
                      <Typography sx={{ fontSize: 11, color: 'var(--stone)', mt: 0.25 }}>
                        {it.item_type === 'pkg' ? 'Package' : 'Test'}
                        {it.tat ? ` · ${it.tat}` : ''}
                      </Typography>
                    </Box>
                    <Typography className="serif" sx={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>
                      ₹{Number(it.price || 0).toLocaleString()}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid var(--border-l)', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Typography sx={{ fontSize: 12, color: 'var(--stone)' }}>
                  Paid via {(booking.payment?.method || 'upi').toUpperCase()}
                  {' · '}Txn {booking.payment?.transaction_id}
                </Typography>
                <Typography className="serif" sx={{ fontSize: 22, fontWeight: 700, color: 'var(--forest)' }}>
                  ₹{Number(booking.amounts?.total || 0).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Right — Timeline + patient info */}
          <Box>
            {/* First-booking nudge — patient has no UHID yet, so they
                can't open the Reports portal. Reassure them this page
                is the place to follow the booking until the lab
                processes their sample (which is when /save-patient-ids
                stamps the UHID and unlocks Reports). */}
            {!user?.uhid && (
              <Box
                sx={{
                  bgcolor: 'var(--mint)',
                  border: '1px dashed var(--sage-p)',
                  borderRadius: '16px',
                  p: 2.5,
                  mb: 2.5,
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'flex-start',
                }}
              >
                <ScienceOutlinedIcon sx={{ color: 'var(--sage)', fontSize: 22, mt: 0.25 }} />
                <Box>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: 'var(--forest)', mb: 0.5 }}>
                    Your reports portal unlocks after sample collection
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: 'var(--stone)', fontWeight: 300, lineHeight: 1.5 }}>
                    Once the lab receives your sample, your <strong>UHID and password</strong> will be sent to your <strong>WhatsApp</strong>. Use them to log in and access your full reports. Until then, this page is the place to follow your booking.
                  </Typography>
                </Box>
              </Box>
            )}

            <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '20px', p: 3, mb: 2.5 }}>
              <Typography className="serif" variant="h3" sx={{ mb: 1, fontSize: 18 }}>
                Live status
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'var(--stone)', mb: 2, fontWeight: 300 }}>
                {etaHint}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 6, borderRadius: 10, mb: 3,
                  bgcolor: 'var(--border-l)',
                  '& .MuiLinearProgress-bar': { bgcolor: 'var(--sage)' },
                }}
              />
              <Stack spacing={2.5}>
                {STAGES.map((s, i) => {
                  const done = i <= stageIdx;
                  const current = i === stageIdx;
                  return (
                    <Stack key={s.key} direction="row" spacing={2} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 28, height: 28, borderRadius: '50%',
                          bgcolor: done ? 'var(--sage)' : 'var(--border-l)',
                          color: done ? '#fff' : 'var(--stone)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                          ...(current && { boxShadow: '0 0 0 5px rgba(61,122,82,0.2)' }),
                        }}
                      >
                        {done ? <CheckIcon sx={{ fontSize: 16 }} /> : (
                          <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{i + 1}</Typography>
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: current ? 700 : 600, color: done ? 'var(--ink)' : 'var(--stone)' }}>
                          {s.label}
                        </Typography>
                        {current && (
                          <Typography sx={{ fontSize: 11, color: 'var(--sage)', fontWeight: 600 }}>
                            ● In progress
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>

            {/* Lab visit — three states:
                  • Pre-collection → hidden (sample isn't with the lab yet).
                  • Sample at lab, not yet registered → soft "awaiting" card so
                    the user knows the gap is normal and self-resolving.
                  • Registered (itdose.visit_id present) → Visit ID + Bill #. */}
            {['collected', 'reported'].includes(booking.status) && (
              booking.itdose?.visit_id ? (
                <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '20px', p: 3, mb: 2.5 }}>
                  <Typography sx={{ fontSize: 11, color: 'var(--stone)', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1 }}>
                    Lab visit
                  </Typography>
                  <Stack spacing={0.75}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ fontSize: 12, color: 'var(--stone)' }}>Visit ID</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', fontFamily: "'JetBrains Mono', monospace" }}>
                        {booking.itdose.visit_id}
                      </Typography>
                    </Stack>
                    {booking.itdose.bill_no && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ fontSize: 12, color: 'var(--stone)' }}>Bill #</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', fontFamily: "'JetBrains Mono', monospace" }}>
                          {booking.itdose.bill_no}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                  <Typography sx={{ fontSize: 11, color: 'var(--stone)', mt: 1.25, fontStyle: 'italic' }}>
                    Show these IDs at the lab if you need to cross-reference your receipt.
                  </Typography>

                  {/* Report download — only when the lab has signed off
                      (status === 'reported'). Opens IT Dose's PDF in a
                      new tab; we log the click via sendBeacon so the
                      view lands in webhook_logs even if the user
                      navigates away in the same gesture. */}
                  {booking.status === 'reported' && (
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<PictureAsPdfOutlinedIcon sx={{ fontSize: 18 }} />}
                      onClick={() => {
                        logReportView({
                          visitId: booking.itdose.visit_id,
                          source:  'track-page',
                        });
                        window.open(
                          buildItdoseReportPdfUrl({ visitId: booking.itdose.visit_id }),
                          '_blank',
                          'noopener,noreferrer',
                        );
                      }}
                      sx={{
                        mt: 2,
                        bgcolor: 'var(--forest)',
                        color: '#fff',
                        borderRadius: 100,
                        py: 1.25,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: 14,
                        '&:hover': { bgcolor: 'var(--ink)' },
                      }}
                    >
                      View report (PDF)
                    </Button>
                  )}
                </Box>
              ) : (
                <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '20px', p: 3, mb: 2.5 }}>
                  <Typography sx={{ fontSize: 11, color: 'var(--stone)', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1 }}>
                    Lab visit
                  </Typography>
                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <Box sx={{ fontSize: 18, lineHeight: 1 }}>⏳</Box>
                    <Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>
                        Awaiting lab registration
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: 'var(--stone)', mt: 0.5, lineHeight: 1.55 }}>
                        Your sample is with the lab. Visit ID and bill number will
                        appear here once the lab logs the prescription — usually
                        within a few hours.
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              )
            )}

            {/* Patient + address */}
            <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '20px', p: 3 }}>
              <Typography sx={{ fontSize: 11, color: 'var(--stone)', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1 }}>
                Collection details
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>
                {booking.patient?.name}
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'var(--stone)', mt: 0.25 }}>
                {booking.patient?.age ? `${booking.patient.age}y · ` : ''}
                {(booking.patient?.gender || 'other').replace(/^./, (c) => c.toUpperCase())}
                {' · '}{booking.patient?.phone}
              </Typography>
              <Typography sx={{ fontSize: 13, color: 'var(--ink)', mt: 1.5, lineHeight: 1.6 }}>
                📍 {booking.patient?.address}
              </Typography>
            </Box>

            {/* Cancel — only when within the 3-hour window */}
            {canCancel(booking) && (
              <Box sx={{ mt: 2.5 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => navigate(buildRoute(ROUTES.CANCEL_BOOKING, { id: booking.booking_id }))}
                  sx={{
                    borderRadius: 100,
                    color: 'var(--red, #DC2626)',
                    borderColor: 'var(--red, #DC2626)',
                    textTransform: 'none', fontWeight: 700, py: 1,
                    '&:hover': { bgcolor: 'rgba(220,38,38,0.06)', borderColor: 'var(--red, #DC2626)' },
                  }}
                >
                  Cancel booking
                </Button>
                <Typography sx={{ fontSize: 11, color: 'var(--stone)', mt: 1, textAlign: 'center' }}>
                  Free cancellation up to 3 hours before your slot
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Track;
